import React, { useCallback } from 'react';
import { useNotifications } from '../../feedback/NotificationProvider';
import type { ItemTagProps, TagSize } from './ItemTag.types';

const sizeClasses: Record<TagSize, string> = {
  xs: 'text-xs px-2 py-1 leading-normal',
  sm: 'text-sm px-2 py-0.5 leading-normal',
  md: 'text-base px-2.5 py-1 leading-normal'
};

/**
 * ItemTag - Exibe ID do item no formato [I-XXX]
 * 
 * Cores: Amarelo/Amber (amber-400) - tom mais escuro para diferenciar de OrderTag
 * Funcionalidade: Click para copiar com toast de confirmação
 */
export const ItemTag: React.FC<ItemTagProps> = ({
  itemId,
  size = 'sm',
  copyable = true,
  title: titleOverride,
  className = ''
}) => {
  const { notify } = useNotifications();

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    if (!copyable) return;
    
    e.stopPropagation();
    
    try {
      await navigator.clipboard.writeText(String(itemId));
      notify({
        type: 'success',
        title: 'ID Copiado',
        message: `Item I-${itemId} copiado para área de transferência`,
        sticky: false
      });
    } catch (err) {
      notify({
        type: 'error',
        title: 'Erro ao copiar',
        message: 'Não foi possível copiar o ID',
        sticky: false
      });
    }
  }, [itemId, copyable, notify]);

  const defaultTitle = copyable ? `Item #${itemId} (clique para copiar)` : `Item #${itemId}`;
  const title = titleOverride ?? defaultTitle;

  return (
    <span
      className={`
        inline-flex items-center font-mono font-semibold 
        rounded border select-none whitespace-nowrap flex-shrink-0
        bg-amber-500/20 text-amber-300 border-amber-400/50
        ${copyable ? 'cursor-pointer hover:bg-amber-500/30 hover:border-amber-400/70 active:scale-95' : ''}
        transition-all duration-150
        ${sizeClasses[size]}
        ${className}
      `}
      title={title}
      onClick={copyable ? handleCopy : undefined}
      role={copyable ? 'button' : undefined}
      tabIndex={copyable ? 0 : undefined}
    >
      I-{itemId}
    </span>
  );
};
